import {createAsyncThunk} from '@reduxjs/toolkit';
import api, {CustomAxiosConfig} from 'services/axios';

type InvoiceParams = {
  vendor_id: string | number;
  invoiceId?: string | number;
  date?: string;
};

export type InvoiceYearsResponse = {
  data: {years: number[]};
};

export type InvoiceResponse = {
  data: {
    id: number;
    order_count: number;
    snapp_commission: number;
    total_sale: number;
    delivery_fee: number;
    vendor_delivery_voucher_share: number;
    snapp_revenue_amount: number;
    payment_amount: number;
    additional_amount: number;
    snapp_vat_amount: number;
    vendor_delivery_coupon_share: number;
    coupon_discount: number;
    is_final: boolean;
    title: string;
    date: string;
  }[];
};

export type InvoiceTransactionResponse = {
  data: {
    additions: {
      id: number;
      price: number;
      reason: string;
    }[];
    deductions: {
      id: number;
      price: number;
      reason: string;
    }[];
  };
};

export type InvoiceReportResponse = {
  data: string;
};

export const getInvoiceYears = createAsyncThunk<InvoiceYearsResponse, InvoiceParams>(
  'getInvoiceYears',
  async (data, thunkApi) => {
    try {
      const response = await api.get(`/dasht/invoice/years?vendor_id=${data.vendor_id}`, {
        goBaseUrl: true
      } as CustomAxiosConfig);
      return thunkApi.fulfillWithValue(response.data);
    } catch (e: any) {
      return thunkApi.rejectWithValue(e);
    }
  }
);

export const getInvoice = createAsyncThunk<InvoiceResponse, InvoiceParams>(
  'getInvoice',
  async (params, thunkApi) => {
    try {
      const response = await api.get(`/dasht/invoice/`, {
        params,
        goBaseUrl: true
      } as CustomAxiosConfig);
      return thunkApi.fulfillWithValue(response.data);
    } catch (e: any) {
      return thunkApi.rejectWithValue(e);
    }
  }
);

export const getInvoiceTransaction = createAsyncThunk<InvoiceTransactionResponse, InvoiceParams>(
  'getInvoiceTransaction',
  async (params, thunkApi) => {
    try {
      const response = await api.get(`/dasht/invoice/${params.invoiceId}/transaction`, {
        params,
        goBaseUrl: true
      } as CustomAxiosConfig);
      return thunkApi.fulfillWithValue(response.data);
    } catch (e: any) {
      return thunkApi.rejectWithValue(e);
    }
  }
);

export const getInvoiceReport = createAsyncThunk<InvoiceReportResponse, InvoiceParams>(
  'getInvoiceReport',
  async (params, thunkApi) => {
    try {
      const response = await api.get(`/dasht/invoice/report`, {
        params,
        goBaseUrl: true,
        responseType: 'blob'
      } as CustomAxiosConfig);

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-report-${params.date}.xlsx`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return thunkApi.fulfillWithValue({data: 'success'});
    } catch (e: any) {
      return thunkApi.rejectWithValue(e);
    }
  }
);
